import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, qs } from '../lib/api.js';
import { BrandSelect } from '../lib/brands.js';
import {
  CHANNEL_LABEL,
  MESSAGE_STATUS_LABEL,
  OPEN_AUTO_LABEL,
  OPEN_HINT,
  OPEN_HUMAN_LABEL,
  SENDER_LABEL,
  label,
} from '../lib/labels.js';
import { dateTime, num } from '../lib/format.js';
import { hrefFor } from '../lib/router.js';
import { EmptyState, ErrorState, Loading, PageHeader, Panel, Tile } from '../components/ui.js';
import { Badge } from '../components/2b-status.js';
import '../screens-2b.css';

interface Sums {
  sent: number;
  delivered: number;
  opened: number;
  clicked: number;
  unsubscribed: number;
  bounced: number;
}

interface SentItem {
  id: string;
  at: string;
  kind: 'service' | 'marketing';
  origin: 'hub' | 'eshop';
  code: string | null;
  template_key: string;
  brand: string | null;
  status: string;
  endpoint: string | null;
  delivered_at: string | null;
  opened_at: string | null;
  opened_human_at: string | null;
  clicked_at: string | null;
  bounced_at: string | null;
  unsubscribed_at: string | null;
  contact_id: string;
  email: string;
}

interface SentResponse {
  page: number;
  pageSize: number;
  total: number;
  sums: { service: Sums; marketing: Sums };
  items: SentItem[];
}

const SUM_LABELS: Array<[keyof Sums, string]> = [
  ['sent', 'Odesláno'],
  ['delivered', 'Doručeno'],
  ['opened', 'Otevřeno'],
  ['clicked', 'Kliknuto'],
  ['unsubscribed', 'Odhlášeno'],
  ['bounced', 'Odskočeno'],
];

const DAYS = [7, 30, 90, 365];
const PAGE_SIZE = 25;

/** Kniha komunikace: skutečně odeslané zprávy servisní i marketingové, z hubu i z e-shopu. */
export function SentTab(): JSX.Element {
  const [brand, setBrand] = useState('');
  const [days, setDays] = useState(30);
  const [kind, setKind] = useState('');
  const [origin, setOrigin] = useState('');
  const [code, setCode] = useState('');
  const [page, setPage] = useState(1);

  const reset =
    <T,>(set: (v: T) => void) =>
    (v: T) => {
      set(v);
      setPage(1);
    };

  const query = useQuery<SentResponse>({
    queryKey: ['sent', brand, days, kind, origin, code, page],
    queryFn: () =>
      api.get(
        `/api/communication/sent${qs({ brand, days, kind, origin, code: code.trim(), page, pageSize: PAGE_SIZE })}`,
      ),
  });

  const data = query.data;
  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <>
      <PageHeader
        level={2}
        title="Odeslané"
        description="Kniha všech skutečně odeslaných e-mailů: servisních i marketingových, z hubu i z e-shopu, s reakcemi příjemců."
      />
      <Panel
        title="Filtr"
        action={
          <div className="row s2b-filters">
            <BrandSelect value={brand} onChange={reset(setBrand)} />
            <select
              value={days}
              aria-label="Období"
              onChange={(e) => reset(setDays)(Number(e.target.value))}
            >
              {DAYS.map((d) => (
                <option key={d} value={d}>
                  Posledních {d} dní
                </option>
              ))}
            </select>
            <select value={kind} aria-label="Druh" onChange={(e) => reset(setKind)(e.target.value)}>
              <option value="">Servis i marketing</option>
              <option value="service">Servis</option>
              <option value="marketing">Marketing</option>
            </select>
            <select
              value={origin}
              aria-label="Původ"
              onChange={(e) => reset(setOrigin)(e.target.value)}
            >
              <option value="">Hub i e-shop</option>
              <option value="hub">Hub</option>
              <option value="eshop">E-shop</option>
            </select>
            <input
              type="search"
              value={code}
              placeholder="kód zprávy"
              aria-label="Kód zprávy"
              onChange={(e) => reset(setCode)(e.target.value)}
              className="s2b-w120"
            />
          </div>
        }
      >
        <p className="note">
          Jen skutečně odeslané zprávy. Zprávy odeslané nanečisto, rezervované a selhané se sem
          nepočítají (ty jsou v kartě zákazníka a v rozhodnutích).
        </p>
      </Panel>

      {query.isLoading ? <Loading what="odeslané zprávy" /> : null}
      {query.isError ? (
        <ErrorState
          title="Odeslané zprávy se nepodařilo načíst"
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      ) : null}

      {data
        ? (['service', 'marketing'] as const).map((k) => (
            <Panel key={k} title={label(CHANNEL_LABEL, k)}>
              <div className="tiles">
                {SUM_LABELS.map(([key, text]) => (
                  <Tile key={key} label={text} value={num(data.sums[k][key])} />
                ))}
              </div>
            </Panel>
          ))
        : null}

      {data ? (
        <Panel title={`Zprávy (${num(data.total)})`}>
          {data.items.length === 0 ? (
            <EmptyState title="V tomto období nic neodešlo">
              Zkuste delší období, jiný druh zpráv nebo smažte kód zprávy ve filtru.
            </EmptyState>
          ) : (
            <div className="scroll">
              <table data-testid="sent-table">
                <thead>
                  <tr>
                    <th>Kdy</th>
                    <th>Druh</th>
                    <th>Původ</th>
                    <th>Kód</th>
                    <th>Značka</th>
                    <th>Příjemce</th>
                    <th>Stav</th>
                    <th>Reakce</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((m) => (
                    <tr key={m.id}>
                      <td>{dateTime(m.at)}</td>
                      <td>
                        <Badge tone={m.kind === 'service' ? 'neu' : 'acc'}>
                          {label(CHANNEL_LABEL, m.kind)}
                        </Badge>
                      </td>
                      <td>{label(SENDER_LABEL, m.origin)}</td>
                      <td>
                        <code>{m.code ?? '—'}</code>
                      </td>
                      <td>{m.brand ?? '—'}</td>
                      <td>
                        <a href={hrefFor('customers', m.contact_id)}>{m.email}</a>
                      </td>
                      <td>{label(MESSAGE_STATUS_LABEL, m.status)}</td>
                      <td>
                        {m.delivered_at ? <Badge tone="ok">doručeno</Badge> : null}
                        {m.opened_at ? (
                          <span title={OPEN_HINT}>
                            <Badge tone="ok">
                              {m.opened_human_at ? OPEN_HUMAN_LABEL : OPEN_AUTO_LABEL}
                            </Badge>
                          </span>
                        ) : null}
                        {m.clicked_at ? <Badge tone="acc">proklik</Badge> : null}
                        {m.unsubscribed_at ? <Badge tone="warn">odhlášení</Badge> : null}
                        {m.bounced_at ? <Badge tone="bad">odskočilo</Badge> : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="row s2b-pager">
            <button className="btn" disabled={page <= 1} onClick={() => setPage(page - 1)}>
              ← Novější
            </button>
            <span className="note">
              Strana {page} z {pages}
            </span>
            <button className="btn" disabled={page >= pages} onClick={() => setPage(page + 1)}>
              Starší →
            </button>
          </div>
        </Panel>
      ) : null}
    </>
  );
}
